// Home screen (`/`) — README §6.1.
//
// Cookpit file input + Open. On Open the file is read as text, parsed
// as JSON, and posted to `/api/validate-recipe`. The course is read
// from `cookpit.courses[0]` in the file itself (the v3.2 spec requires
// at least one entry); the user no longer declares it up-front, so the
// previous COURSE_MISMATCH guard is unreachable from the UI.
// Successful validation populates the store and navigates to /prereqs.
// Rejection surfaces the calm message + recovery inline; the file
// remains so the user can swap it without re-uploading.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ColourToggle, Wordmark } from '../design/components';
import { theme } from '../design/theme';
import { validateRecipe } from '../api/client';
import { ApiError } from '../api/errors';
import { useAppStore } from '../state/store';
import { showKofiOverlay } from '../kofiOverlay';
import type { Course } from '@cookchow/shared';

const ALLOWED_COURSES: readonly Course[] = ['starter', 'main', 'dessert'] as const;

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

// Extract the file's declared primary course. Returns null if the file
// shape doesn't carry a usable `cookpit.courses[0]` — in which case we
// let the server respond with its calm error instead of guessing.
function readPrimaryCourse(recipe: unknown): Course | null {
  if (!isObject(recipe)) return null;
  const cookpit = recipe.cookpit;
  if (!isObject(cookpit)) return null;
  const courses = cookpit.courses;
  if (!Array.isArray(courses)) return null;
  const first = courses[0];
  if (typeof first !== 'string') return null;
  return (ALLOWED_COURSES as readonly string[]).includes(first) ? (first as Course) : null;
}

export default function Home() {
  const navigate = useNavigate();
  const recipeFile = useAppStore((s) => s.recipeFile);
  const setRecipeFile = useAppStore((s) => s.setRecipeFile);
  const validationError = useAppStore((s) => s.validationError);
  const setValidationError = useAppStore((s) => s.setValidationError);
  const setValidatedRecipe = useAppStore((s) => s.setValidatedRecipe);
  const colorScheme = useAppStore((s) => s.colorScheme);
  const setColorScheme = useAppStore((s) => s.setColorScheme);
  const [busy, setBusy] = useState(false);

  // Floating Ko-fi "Support Us" widget — Home only. Shown on mount, hidden
  // again on unmount so it never overlaps the calm cook surfaces.
  useEffect(() => showKofiOverlay(), []);

  const canOpen = !!recipeFile && !busy;

  async function onOpen(): Promise<void> {
    if (!recipeFile) return;
    setBusy(true);
    setValidationError(null);
    try {
      // FileReader rather than `File.prototype.text()` for broader runtime
      // coverage — jsdom under Vitest does not implement `.text()`.
      const text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string) ?? '');
        reader.onerror = () => reject(reader.error ?? new Error('read'));
        reader.readAsText(recipeFile);
      });
      let recipe: unknown;
      try {
        recipe = JSON.parse(text);
      } catch {
        setValidationError({
          code: 'INVALID_JSON',
          message: "This file isn't valid JSON.",
          recovery: 'Choose another?',
        });
        return;
      }
      const course = readPrimaryCourse(recipe);
      if (!course) {
        setValidationError({
          code: 'NOT_COOKPIT_FILE',
          message: "This file doesn't look like a cookpit v3.2 recipe.",
          recovery: 'Choose another?',
        });
        return;
      }
      const result = await validateRecipe({ recipe, selectedCourse: course });
      if (result.valid) {
        setValidatedRecipe({
          recipe: recipe as Parameters<typeof setValidatedRecipe>[0]['recipe'],
          recipeId: result.recipeId,
          meta: result.meta,
        });
        navigate('/prereqs');
      } else {
        // Show the first error verbatim — the server has authored calm copy.
        const first = result.errors[0] ?? {
          code: 'BAD_REQUEST',
          message: 'This file could not be read.',
          recovery: 'Choose another?',
        };
        setValidationError(first);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setValidationError({ code: err.code, message: err.message, recovery: err.recovery });
      } else {
        setValidationError({
          code: 'NETWORK',
          message: "Couldn't reach the cooking service — check your connection.",
          recovery: 'Try again in a moment.',
        });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main
      className="cookchow-home"
      style={{
        background: theme.bg.base,
        color: theme.text.primary,
        flexDirection: 'column',
        gap: '1.5rem',
        maxWidth: '32rem',
        margin: '0 auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}
      >
        <a
          href="https://cookpit.recipes"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Recipes — opens cookpit.recipes in a new tab"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.3rem',
            minHeight: '44px',
            padding: '0 0.9rem',
            background: theme.bg.raised,
            color: theme.text.primary,
            border: `1px solid ${theme.bg.muted}`,
            borderRadius: '8px',
            fontSize: '0.85rem',
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          Recipes
          <span aria-hidden="true">↗</span>
        </a>
        <ColourToggle
          value={colorScheme}
          onChange={setColorScheme}
          variant="rectangle"
        />
      </div>

      <Wordmark size="lg" as="h1" />
      <p
        style={{
          margin: '-1rem 0 0',
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'center',
          gap: '0.4rem',
          lineHeight: 1,
        }}
      >
        <span
          style={{
            fontFamily: '"Caveat", cursive',
            fontStyle: 'italic',
            fontWeight: 600,
            fontSize: '1.6rem',
            color: theme.accent.action,
          }}
        >
          Single File
        </span>
        <span
          style={{
            fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: '0.08em',
            color: theme.text.muted,
          }}
        >
          BETA
        </span>
      </p>
      <p
        style={{
          margin: 0,
          color: theme.text.muted,
          fontSize: '1rem',
          textAlign: 'center',
        }}
      >
        {"Real-time 'live' recipe cooking"}
      </p>

      <label
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          padding: '1rem',
          background: theme.bg.raised,
          borderRadius: '10px',
        }}
      >
        <span style={{ fontWeight: 600, color: theme.text.primary }}>Choose your recipe file</span>
        <input
          type="file"
          accept=".jsonld"
          onChange={(e) => setRecipeFile(e.target.files?.[0] ?? null)}
          aria-label="Choose your recipe file"
        />
        <span style={{ fontSize: '0.85rem', color: theme.text.muted }}>A cookpit v3.2 file.</span>
        {validationError && (
          <div
            role="alert"
            style={{
              marginTop: '0.5rem',
              padding: '0.75rem',
              background: theme.bg.muted,
              borderRadius: '8px',
              color: theme.text.primary,
            }}
          >
            <div style={{ fontWeight: 600 }}>{validationError.message}</div>
            <div style={{ fontSize: '0.9rem', color: theme.text.muted, marginTop: '0.25rem' }}>
              {validationError.recovery}
            </div>
          </div>
        )}
      </label>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.5rem',
          minHeight: '44px',
        }}
      >
        {busy ? (
          <div
            role="status"
            aria-live="polite"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: theme.text.muted,
              fontSize: '0.95rem',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                border: `2px solid ${theme.accent.calm}`,
                borderTopColor: 'transparent',
                display: 'inline-block',
              }}
            />
            Reading your recipe…
          </div>
        ) : (
          <Button variant="primary" state={canOpen ? 'enabled' : 'disabled'} onClick={onOpen}>
            Open
          </Button>
        )}

        {/* Square, icon-only docs affordance sitting to the right of Open —
            same transparent 44×44 icon-button shape as ChefHat. Opens the
            cookpit docs site in a new tab; the label carries the meaning
            since the glyph is decorative (aria-hidden). */}
        <a
          href="https://cookpit.org"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Documentation — opens cookpit.org in a new tab"
          style={{
            width: '44px',
            height: '44px',
            padding: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            color: theme.text.primary,
            textDecoration: 'none',
          }}
        >
          <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            {/* A minimal document glyph: page with a folded corner and lines. */}
            <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
            <path d="M14 3v5h5" />
            <path d="M9 13h6" />
            <path d="M9 17h6" />
          </svg>
        </a>
      </div>

      {/* Support is offered by the floating Ko-fi widget mounted via
          showKofiOverlay() (Home only) — no inline button here. */}

      <footer
        style={{
          textAlign: 'center',
          fontSize: '0.85rem',
          color: theme.text.muted,
        }}
      >
        © 2026{' '}
        <a
          href="https://broadbaseai.com"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="BroadbaseAI — opens broadbaseai.com in a new tab"
          style={{ color: theme.accent.action, textDecoration: 'none', fontWeight: 600 }}
        >
          BroadbaseAI
        </a>
      </footer>
    </main>
  );
}
